import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { fields } from '../../Service/CaseConceptualization/caseFields';
import { getConceptualization, requestConceptualizationOverview, submitConceptualizationField } from '../AIClients/aiClientData';
import CaseFieldTabs, { OVERVIEW_TAB } from './CaseFieldTabs';
import CaseFieldEditor from './CaseFieldEditor';
import CaseOverview from './CaseOverview';
import CaseGuidePanel from './CaseGuidePanel';
import CaseOverviewPanel from './CaseOverviewPanel';
import CaseTranscriptPanel from './CaseTranscriptPanel';

// 현재 예시 면담은 MI입니다. 공통·위험 항목은 모든 관점에서 사용합니다.
export function fieldsForLens(lens) {
    return [...fields.filter(field => field.lens === '공통' || field.lens === 'Stage 0 플래그' || field.lens.includes(lens)),
        { key: 'plan', label: '상담 목표와 다음 탐색', helper: '내담자와 함께 정할 상담 목표와 다음 면담에서 더 탐색할 내용을 적어주세요.' }];
}
export const miFields = fieldsForLens('MI');

const NO_SESSION_MESSAGE = '저장된 면담이 없어 AI 피드백을 받을 수 없습니다. 작성한 내용은 이 브라우저에 저장되었습니다.';
const NO_SESSION_OVERVIEW_MESSAGE = '저장된 면담이 없어 연결 점검을 받을 수 없습니다.';

// 면담별 초안 저장 키. 면담 정보가 없으면 예시용 키를 쓴다.
export function caseStorageKeyFor(saved) {
    return `garam-case-practice:${saved?.id ?? 'sample-01'}:mi:v1`;
}

// 이 브라우저에 저장한 사례개념화 초안을 읽는다.
export function loadDraft(storageKey) {
    const empty = Object.fromEntries(miFields.map(field => [field.key, '']));
    try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
        if (!saved || saved.version !== 1 || !saved.values || typeof saved.values !== 'object') return { values: empty, notice: '' };
        return { values: Object.fromEntries(miFields.map(field => [field.key, typeof saved.values[field.key] === 'string' ? saved.values[field.key].slice(0, 2000) : ''])), notice: '이 브라우저에 저장한 내용을 불러왔습니다.' };
    } catch {
        return { values: empty, notice: '저장한 내용을 불러오지 못했습니다. 새로 작성할 수 있습니다.' };
    }
}

// 필수 항목 중 아직 비어 있는 항목을 돌려준다.
export function missingRequiredFields(values) {
    return miFields.filter(field => field.required && !values[field.key]?.trim());
}

// 비어 있는 항목만 서버 값으로 채운다. 브라우저에서 작성 중인 내용은 덮어쓰지 않는다.
function fillEmptyValues(current, serverValues) {
    return Object.fromEntries(Object.entries(current).map(([key, value]) => [key, value.trim() ? value : (serverValues[key] || '')]));
}

// 서버의 항목별 피드백을 현재 피드백 상태에 합친다. keep(key)가 true인 항목은 그대로 둔다.
function mergeServerFeedbacks(current, result, keep) {
    const next = { ...current };
    Object.entries(result.fields).forEach(([key, data]) => {
        if (!keep(key)) next[key] = { status: 'done', text: (result.values[key] || '').trim(), data };
    });
    return next;
}

// 화면 글과 서버 저장본이 다른 항목(서버에 저장하지 않은 항목)을 돌려준다.
function unsentFields(values, submitted) {
    return miFields.filter(field => values[field.key].trim() !== (submitted[field.key] || '').trim());
}

// 현재 탭 다음에 열 탭을 정한다. 마지막 항목 다음은 전체 보기.
function nextTabKey(activeKey) {
    const index = miFields.findIndex(field => field.key === activeKey);
    return index >= 0 && index < miFields.length - 1 ? miFields[index + 1].key : OVERVIEW_TAB;
}

// 탭으로 항목을 골라 작성하고, 저장하면 오른쪽에 AI 피드백을 받는 사례개념화 작업 화면.
export default function CasePracticeForm({ saved, clientName }) {
    const storageKey = caseStorageKeyFor(saved);
    const [initial] = useState(() => loadDraft(storageKey));
    const [draft, setDraft] = useState(initial.values);
    const [notice, setNotice] = useState(initial.notice);
    const [saveError, setSaveError] = useState('');
    const [activeKey, setActiveKey] = useState(miFields[0].key);
    const [feedbacks, setFeedbacks] = useState({});
    const [submitted, setSubmitted] = useState({});
    const [overview, setOverview] = useState(null);
    const activeField = miFields.find(field => field.key === activeKey);

    // 들어올 때 서버에 저장된 항목 글과 피드백을 복원한다.
    useEffect(() => {
        if (!saved) return undefined;
        let active = true;
        setNotice('서버에 저장된 사례개념화를 불러오고 있습니다.');
        getConceptualization(saved.id, saved.token).then(result => {
            if (!active) return;
            if (!result) return setNotice('');
            setSubmitted(result.values);
            setDraft(current => fillEmptyValues(current, result.values));
            if (result.coherence) setOverview(current => current || { status: 'done', data: result.coherence, snapshot: JSON.stringify(result.values) });
            setFeedbacks(current => mergeServerFeedbacks(current, result, key => Boolean(current[key])));
            setNotice('서버에 저장된 사례개념화를 불러왔습니다.');
        }).catch(failure => {
            if (active) setNotice(`서버에 저장된 내용을 불러오지 못했습니다. ${failure.message}`);
        });
        return () => { active = false; };
    }, [saved]);

    // 입력값을 바꾸고 저장 안내를 갱신한다.
    function updateField(key, value) {
        setDraft(current => ({ ...current, [key]: value }));
        setNotice('저장하지 않은 변경 사항이 있습니다.');
        setSaveError('');
    }

    // 현재 초안을 이 브라우저에 저장한다. 성공하면 true를 돌려준다.
    function storeDraft() {
        try {
            localStorage.setItem(storageKey, JSON.stringify({ version: 1, values: draft }));
            setSaveError('');
            setNotice('저장했습니다. 새로고침하거나 다시 방문해도 이 브라우저에서 이어 쓸 수 있습니다.');
            return true;
        } catch {
            setNotice('');
            setSaveError('저장하지 못했습니다. 브라우저 저장 공간과 설정을 확인해주세요. 작성 중인 내용은 화면에 유지됩니다.');
            return false;
        }
    }

    // 한 항목의 피드백 상태를 바꾼다. undefined면 피드백을 지운다.
    function setFieldFeedback(key, feedback) {
        setFeedbacks(current => ({ ...current, [key]: feedback }));
    }

    // 서버가 돌려준 항목별 피드백을 반영한다. 방금 보낸 항목은 보낸 글을 기준으로 둔다.
    function applyServerFeedback(result, key, text) {
        setFeedbacks(current => ({
            ...mergeServerFeedbacks(current, result, fieldKey => current[fieldKey]?.status === 'loading'),
            [key]: { status: 'done', text, data: result.fieldFeedback },
        }));
    }

    // 한 항목을 서버에 저장하고 그 항목의 AI 피드백을 받는다.
    async function loadFeedback(key) {
        const text = draft[key].trim();
        if (!text) return setFieldFeedback(key, undefined);
        if (!saved) return setFieldFeedback(key, { status: 'error', text, error: NO_SESSION_MESSAGE, retryable: false });
        setFieldFeedback(key, { status: 'loading', text });
        try {
            const result = await submitConceptualizationField(saved.id, saved.token, { field: key, text });
            setSubmitted(result.values);
            applyServerFeedback(result, key, text);
        } catch (failure) {
            setFieldFeedback(key, { status: 'error', text, error: failure.message, retryable: true });
        }
    }

    // 서버에 저장된 항목들이 서로 이어지는지 AI 점검을 받는다. 점검 당시 저장본을 함께 보관한다.
    async function loadOverview() {
        if (!saved) return setOverview({ status: 'error', error: NO_SESSION_OVERVIEW_MESSAGE, retryable: false });
        const snapshot = JSON.stringify(submitted);
        setOverview({ status: 'loading' });
        try {
            const data = await requestConceptualizationOverview(saved.id, saved.token);
            setOverview({ status: 'done', data, snapshot });
        } catch (failure) {
            setOverview({ status: 'error', error: failure.message, retryable: true });
        }
    }

    // 저장하고, 항목 탭이면 그 항목의 피드백을, 전체 보기면 연결 점검을 요청한다.
    function save(event) {
        event.preventDefault();
        if (!storeDraft()) return;
        if (activeKey === OVERVIEW_TAB) {
            if (overview?.status !== 'loading') loadOverview();
            return;
        }
        if (feedbacks[activeKey]?.status !== 'loading') loadFeedback(activeKey);
    }

    const pending = activeField ? feedbacks[activeKey]?.status === 'loading' : overview?.status === 'loading';

    return <form className="case-workspace" onSubmit={save} aria-labelledby="case-title">
        <div className="review-card case-workspace-head">
            <span className="ai-kicker">CASE CONCEPTUALIZATION</span>
            <div className="review-row"><h2 id="case-title">흩어진 이야기를 연결하기</h2><span className="review-tag">MI · 동기면담</span></div>
            <p className="ai-small">항목을 하나씩 골라 작성하고 저장하면 오른쪽에 AI 피드백이 나타납니다. 마지막에 전체 보기에서 항목끼리 이어지는지 확인하세요.</p>
            <CaseFieldTabs fields={miFields} values={draft} activeKey={activeKey} onSelect={setActiveKey} />
        </div>
        <CaseTranscriptPanel saved={saved} clientName={clientName} highlightIndexes={feedbacks[activeKey]?.data?.evidenceMessageIndexes} />
        <section className="review-card case-editor" aria-label={activeField ? `${activeField.label} 작성` : '전체 보기'}>
            {activeField
                ? <CaseFieldEditor field={activeField} value={draft[activeKey]} onChange={updateField} />
                : <CaseOverview fields={miFields} values={draft} onEdit={setActiveKey} />}
            <p className="ai-small">작성 내용은 현재 브라우저에 보관됩니다. 저장하고 피드백을 받으면 그 항목이 서버에도 저장됩니다.</p>
            <div className="review-actions">
                <span className="ai-small" role="status">{notice}</span>
                <div className="case-editor-buttons">
                    {activeField && <button type="button" className="review-secondary" onClick={() => setActiveKey(nextTabKey(activeKey))}>다음 항목 →</button>}
                    <button className="review-primary" type="submit" disabled={pending}>{activeField ? '저장하고 피드백 받기' : '연결 점검 받기'}</button>
                </div>
            </div>
            {saveError && <p className="ai-alert" role="alert">{saveError}</p>}
        </section>
        {activeField
            ? <CaseGuidePanel activeKey={activeKey} feedback={feedbacks[activeKey]} currentText={draft[activeKey]} onRetry={() => loadFeedback(activeKey)} />
            : <CaseOverviewPanel overview={overview} outdated={overview?.status === 'done' && overview.snapshot !== JSON.stringify(submitted)} unsentLabels={unsentFields(draft, submitted).map(field => field.label)} onRetry={loadOverview} />}
    </form>;
}

CasePracticeForm.propTypes = {
    saved: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), token: PropTypes.string }),
    clientName: PropTypes.string,
};
